/**
 * Ananas — Paladin (Ananias). LMAO-Style (BATTLE-17):
 * "goldener Schildwall mit heiliger Krone" — breiter, standfester Tonnen-Body
 * (Tank-Silhouette: niedriger Schwerpunkt, docs/13 §3) in Goldgelb mit klar
 * lesbarer Rauten-Kreuzschraffur (dunkle Furchen mit Licht-Kante + helle
 * Feld-Glanzpunkte). Obendrauf eine fuenfblaettrige GRUENE Blattkrone als
 * Heiligenschein/Helm-Fan (Tank-Support-Motiv).
 * 3★: goldener Heilig-Schimmer (#ffd94a, docs/12 §2) auf den Rauten-Kreuzungen,
 * pulsiert mit pose.castPulse; Cast laesst zusaetzlich einen Halo hinter der
 * Krone aufgluehen.
 * Referenz-Qualitaet: kohlrabi.ts / okra.ts. Contract: species/types.ts.
 */
import type { PaintParams, SpeciesPainter } from './types'
import { darken, glow, lighten, rgba, rimLight, specular, volumeGradient } from '../draw3d'
import { KEYWORD_COLORS } from '../../ui/theme'

/** verbindliche Heilig-Farbe (docs/12 §2) — nur fuer Schimmer/Glow, nie fuer den Koerper */
const HOLY = KEYWORD_COLORS.holy

export const paintAnanas: SpeciesPainter = (p: PaintParams) => {
  const { ctx, x, y, r, colors, tier, pose } = p
  const dead = p.emotion === 'dead'
  const outlineW = Math.max(2, r * 0.12)

  ctx.save()
  ctx.lineJoin = 'round'
  ctx.translate(x, y)

  // --- Blattkrone (hinter dem Korpus): 5 spitze Blaetter als Halo/Helm-Fan ---
  const crownBaseY = -r * 0.66
  const leaf = (ang: number, len: number, w: number, tone: number): void => {
    ctx.save()
    ctx.translate(0, crownBaseY)
    ctx.rotate(ang)
    // Volumen entlang des Blatts: dunkle Basis -> helle Spitze (Licht oben-links)
    const g = ctx.createLinearGradient(-w, 0, w * 0.5, -len)
    g.addColorStop(0, darken(colors.accent, 18 - tone))
    g.addColorStop(0.55, lighten(colors.accent, tone))
    g.addColorStop(1, lighten(colors.accent, tone + 18))
    ctx.fillStyle = g
    ctx.strokeStyle = colors.outline
    ctx.lineWidth = Math.max(1.5, r * 0.08)
    ctx.beginPath()
    ctx.moveTo(-w, 0)
    ctx.quadraticCurveTo(-w * 1.08, -len * 0.52, 0, -len)
    ctx.quadraticCurveTo(w * 1.08, -len * 0.52, w, 0)
    ctx.closePath()
    ctx.fill()
    ctx.stroke()
    // Licht-Kante an der linken Blattflanke (Lichtquelle oben-links)
    if (!dead) {
      ctx.strokeStyle = rgba(lighten(colors.accent, 36), 0.75)
      ctx.lineWidth = Math.max(1, r * 0.035)
      ctx.beginPath()
      ctx.moveTo(-w * 0.6, -len * 0.14)
      ctx.quadraticCurveTo(-w * 0.62, -len * 0.56, 0, -len * 0.9)
      ctx.stroke()
    }
    ctx.restore()
  }
  // aussen -> innen zeichnen (Mittelblatt zuletzt = vorne); breiter Fan = Helm/Halo
  leaf(-1.0, r * 0.72, r * 0.2, 2)
  leaf(1.0, r * 0.72, r * 0.2, 2)
  leaf(-0.5, r * 1.0, r * 0.23, 8)
  leaf(0.5, r * 1.0, r * 0.23, 8)
  leaf(0, r * 1.2, r * 0.26, 14)

  // Cast: heiliger Schein hinter der Krone (Support-Halo-Motiv, docs/13 §5 sparsam)
  if (!dead && pose.castPulse > 0.02) {
    glow(ctx, 0, -r * 0.95, r * 0.95, HOLY, 0.32 * pose.castPulse)
  }

  // --- Korpus: breite, standfeste Tonne — unten am breitesten (Tank-Schwerpunkt) ---
  const bodyPath = (): void => {
    ctx.beginPath()
    ctx.moveTo(0, -r * 0.86)
    ctx.bezierCurveTo(r * 0.58, -r * 0.84, r * 0.9, -r * 0.44, r * 0.97, r * 0.12)
    ctx.bezierCurveTo(r * 1.02, r * 0.58, r * 0.62, r * 0.95, 0, r * 0.97)
    ctx.bezierCurveTo(-r * 0.62, r * 0.95, -r * 1.02, r * 0.58, -r * 0.97, r * 0.12)
    ctx.bezierCurveTo(-r * 0.9, -r * 0.44, -r * 0.58, -r * 0.84, 0, -r * 0.86)
    ctx.closePath()
  }

  ctx.strokeStyle = colors.outline
  ctx.lineWidth = outlineW
  bodyPath()
  ctx.fillStyle = volumeGradient(ctx, 0, 0, r, colors.body)
  ctx.fill()
  ctx.stroke()

  // --- Schalen-Muster, geclippt auf den Korpus ---
  ctx.save()
  bodyPath()
  ctx.clip()

  // Rauten-Kreuzschraffur: zwei Familien diagonaler Furchen, am Aequator nach
  // aussen gewoelbt (Kontrollpunkt xi*1.35) = Wrap-around-Eindruck der Tonne
  const hatch = (ang: number): void => {
    ctx.save()
    ctx.rotate(ang)
    ctx.lineCap = 'round'
    for (let i = -2; i <= 2; i++) {
      const xi = i * r * 0.4
      // dunkle Furche
      ctx.strokeStyle = rgba(darken(colors.body, 34), 0.95)
      ctx.lineWidth = Math.max(1.4, r * 0.07)
      ctx.beginPath()
      ctx.moveTo(xi, -r * 1.2)
      ctx.quadraticCurveTo(xi * 1.35, 0, xi, r * 1.2)
      ctx.stroke()
      // schmale Licht-Kante neben der Furche (Bevel-Eindruck der Schuppen)
      ctx.strokeStyle = rgba(lighten(colors.body, 24), dead ? 0.25 : 0.55)
      ctx.lineWidth = Math.max(1, r * 0.028)
      ctx.beginPath()
      ctx.moveTo(xi - r * 0.05, -r * 1.2)
      ctx.quadraticCurveTo(xi * 1.35 - r * 0.05, -r * 0.03, xi - r * 0.05, r * 1.2)
      ctx.stroke()
    }
    ctx.restore()
  }
  hatch(0.6)
  hatch(-0.6)

  // helle Feld-Glanzpunkte in den Rauten — nur Flanken/Unterseite,
  // die Gesichtszone (um y-r*0.18, Renderer-Layer 6) bleibt ruhig
  ctx.fillStyle = rgba(lighten(colors.body, 32), dead ? 0.25 : 0.7)
  for (const [gx, gy] of [
    [0, 0.56], [-0.5, 0.32], [0.52, 0.3], [-0.68, -0.16], [0.7, -0.14], [0.02, 0.1],
  ] as const) {
    ctx.beginPath()
    ctx.ellipse(gx * r, gy * r, r * 0.062, r * 0.04, -0.5, 0, Math.PI * 2)
    ctx.fill()
  }

  // Boden-Kontaktschatten im Muster: verankert den niedrigen Schwerpunkt
  ctx.fillStyle = rgba(darken(colors.body, 32), 0.24)
  ctx.beginPath()
  ctx.ellipse(r * 0.08, r * 0.88, r * 0.8, r * 0.24, 0, 0, Math.PI * 2)
  ctx.fill()

  // 3★: goldener Heilig-Schimmer auf den Rauten-Kreuzungen (pulst mit castPulse)
  if (tier >= 3 && !dead) {
    const pulse = 0.62 + pose.castPulse * 0.38
    ctx.save()
    ctx.lineCap = 'round'
    ctx.strokeStyle = rgba(HOLY, pulse)
    ctx.fillStyle = rgba(HOLY, pulse)
    ctx.shadowColor = HOLY
    ctx.shadowBlur = r * (0.16 + pose.castPulse * 0.16)
    for (const [sx, sy, s] of [
      [-0.27, 0.46, 1], [0.3, 0.42, 0.85], [-0.6, 0.04, 0.7],
      [0.62, 0.06, 0.7], [0.02, 0.82, 0.75],
    ] as const) {
      const cxp = sx * r
      const cyp = sy * r
      const a = r * 0.085 * s
      // 4-Punkt-Funkel: Kreuz + Kern
      ctx.lineWidth = Math.max(1, r * 0.035)
      ctx.beginPath()
      ctx.moveTo(cxp - a, cyp)
      ctx.lineTo(cxp + a, cyp)
      ctx.moveTo(cxp, cyp - a)
      ctx.lineTo(cxp, cyp + a)
      ctx.stroke()
      ctx.beginPath()
      ctx.arc(cxp, cyp, r * 0.03 * s, 0, Math.PI * 2)
      ctx.fill()
    }
    ctx.restore()
  }
  ctx.restore() // Clip Ende

  // Outline nach dem Muster knackig nachziehen (Silhouetten-Readability)
  ctx.strokeStyle = colors.outline
  ctx.lineWidth = outlineW
  bodyPath()
  ctx.stroke()

  // --- Licht: Rim oben-links + Specular (nie auf Toten) ---
  if (!dead) {
    rimLight(ctx, 0, 0.02 * r, r * 0.95, r * 0.9, 'rgba(255, 250, 230, 0.5)', Math.max(1.5, r * 0.07))
    specular(ctx, -r * 0.36, -r * 0.44, r * 0.15, 0.5)
  }

  ctx.restore()
}
